import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"
import { Modal } from "@/componentes/ui/Modal"
import { comoFechaHora } from "@/lib/fechas"

import type { ApunteBitacora, Credencial } from "./tipos"

const ACCIONES: Record<string, string> = {
  "credencial.crear": "La registró",
  "credencial.editar": "La cambió",
  "credencial.revelar": "Vio la contraseña",
  "credencial.eliminar": "La borró",
}

/**
 * Quién la registró, quién la cambió y quién vio la contraseña.
 *
 * ⚠ **Es lo que hace revisable el poder de revelar.** Sin esto, «quién sabía la clave
 * del banco el día que se usó» no tendría respuesta. Sale de la bitácora de auditoría,
 * que no se edita ni se borra.
 */
export function BitacoraCredencial({
  credencial,
  onCerrar,
}: {
  credencial: Credencial | null
  onCerrar: () => void
}) {
  const apuntes = useQuery({
    queryKey: ["credenciales", "bitacora", credencial?.id],
    queryFn: () => api.get<ApunteBitacora[]>(`/api/v1/credenciales/${credencial?.id}/bitacora`),
    enabled: credencial !== null,
  })

  return (
    <Modal
      abierto={credencial !== null}
      titulo={`Bitácora de «${credencial?.nombre ?? ""}»`}
      descripcion="Las últimas 50 cosas que le pasaron a esta credencial."
      onCerrar={onCerrar}
    >
      {apuntes.isPending ? (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      ) : apuntes.isError ? (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar la bitácora: {apuntes.error.message}
        </p>
      ) : apuntes.data.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Sin movimientos.
        </p>
      ) : (
        <ol className="divide-y text-sm">
          {apuntes.data.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2"
              style={{ borderColor: "var(--border)" }}
            >
              <span>
                <strong className="font-medium">{a.actor ?? "Alguien que ya no está"}</strong> ·{" "}
                {ACCIONES[a.accion] ?? a.accion}
              </span>
              <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                {comoFechaHora(a.creado_en)}
                {a.ip ? ` · ${a.ip}` : ""}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  )
}
